import React, { useMemo, useState } from 'react';

const favicon = (domain, sz = 64) => `https://www.google.com/s2/favicons?domain=${domain}&sz=${sz}`;
const placehold = (letter, size = '20x20') => `https://placehold.co/${size}/0f172a/ffffff?text=${letter}`;

/* ─── Layer detail data (Tab 2 sidebar) ─── */
const LAYERS = {
  1: {
    title: 'Layer 1: IIoT-Enabled CapEx & MRO',
    desc: 'An asset-heavy machinery protection engine. Sourcing integrates machinery vibration telemetry directly to ERP bids. If a steel mill rolling bearing or mining shovel is close to failure, the system autonomously issues strategic spares RFQs to global hubs.',
    regulatory: "Strategic Pathway: Program executed under 'Predictive Strategic MRO Procurement' roadmap within Tab 3.",
    moat: 'Semantic Data Cleanse Engines & CMMS/IIoT Machine Vision Triggering.',
    context: 'The asset must have a robust semantic parser to translate duplicate parts catalogs from multiple factories into a single inventory master.',
    stat: 'Maintenance Security (Minimizing Down-cycles)',
    icon: 'fa-solid fa-screwdriver-wrench',
    type: 'Layer Analysis',
  },
  2: {
    title: 'Layer 2: Real-Time Fleet & Logistics Telemetry',
    desc: 'Secures global bulk mineral shipments. Maps ocean freight routes, rail constraints, and local truck grids inside real-time telemetries, eliminating port demurrage idle fine leaks.',
    regulatory: "Strategic Pathway: Programs executed via 'Predictive Multimodal Freight' and 'Demurrage Yard Automation' mapped in Tab 3.",
    moat: 'High-Fidelity Multi-Modal Maritime, Rail & ELD Sensor Integration.',
    context: 'The software must integrate natively with maritime transponders and railroad network systems to predict port gate-in delays and eliminate detention penalties.',
    stat: 'Logistical Flow (Port-to-Plant Speed)',
    icon: 'fa-solid fa-truck-ramp-box',
    type: 'Layer Analysis',
  },
  3: {
    title: 'Layer 3: AI-Driven Metallurgical & Energy IBOM',
    desc: 'Algorithmic furnace raw materials charge models. Shifting input recipes (limestone, coal, metals, energy) hourly relative to regional spot spreads to lock bottom-line margins.',
    regulatory: "Strategic Pathway: Programs executed via 'Dynamic Blast Charge Optimization' and 'Grid Load Hedging' mapped in Tab 3.",
    moat: 'Generative Materials Chemistry & Grid-Load Market API Trading.',
    context: 'The technology must be capable of dynamically altering physical chemical recipe mixes on-the-fly while executing automated utility spot trades.',
    stat: 'Material Arbitrage (Charge Recipe Shifting)',
    icon: 'fa-solid fa-vial-reconciliation',
    type: 'Layer Analysis',
  },
  4: {
    title: 'Layer 4: Agentic Compliance & Carbon Arbitrage',
    desc: 'Establishes a solid custom regulatory barrier over carbon-heavy imports. Implements Scope 3 carbon data collection from primary miners, backing low-emissions GFG premium claims.',
    regulatory: "Strategic Pathway: Programs executed via 'CBAM Borders Customs Shield' and 'Scope 3 emissions ledgers' mapped in Tab 3.",
    moat: 'Deep-Tier Carbon Accounting & Graph Database Lineage.',
    context: 'The asset must be able to trace carbon metrics to the individual furnace run, rather than just generating static monthly estimates.',
    stat: 'Compliance Armor (CBAM Customs Defense)',
    icon: 'fa-solid fa-smog',
    type: 'Layer Analysis',
  },
};

/* ─── Player detail data (Tab 2 sidebar) ─── */
const PLAYERS = {
  sap: { title: 'SAP Ariba', desc: 'The definitive cloud platform for international enterprise transactions, purchase orders, and supplier catalogs.', regulatory: 'Sox / GxP Auditable Baseline.', stat: 'Market Defender', icon: 'fa-solid fa-building', type: 'Vendor Profile' },
  procurepro: { title: 'ProcurePro', desc: 'An advanced cloud suite designed to evaluate, contract, and compare multi-tier capital infrastructure and CapEx project tenders.', regulatory: 'ISO 27001 Certified Security.', stat: 'AI Insurgent', icon: 'fa-solid fa-rocket', type: 'Vendor Profile' },
  carbonchain: { title: 'CarbonChain', desc: 'Calculates precise carbon intensity per ton of steel/aluminum. CBAM Exposure Calculator models certificate surrendering.', regulatory: 'GHG Protocol / CBAM Audit Compliant.', stat: 'AI Insurgent', icon: 'fa-solid fa-leaf', type: 'Venture Profile' },
  exiger: { title: 'Exiger (1Exiger)', desc: 'Military-grade compliance shield tracing raw mineral components down to the mine of origin to protect against embargoes.', regulatory: 'ITAR / Defense Sourcing Standards.', stat: 'Market Defender (PE Backed)', icon: 'fa-solid fa-shield-halved', type: 'Venture Profile' },
  carbmee: { title: 'carbmee (EIS™)', desc: 'Integrates into plant ERPs to build audit-ready transactional Scope 3 ledgers for multi-plant manufacturing.', regulatory: 'CSRD / CBAM / EUDR Aligned.', stat: 'AI Insurgent', icon: 'fa-solid fa-smog', type: 'Venture Profile' },
  tsc: { title: 'The Sustainability Cloud', desc: 'Maps complex manufacturing emissions, allowing non-EU exporters to present clean data and hedge carbon certificate costs.', regulatory: 'CBAM / BRSR / ISO 14064 Compliant.', stat: 'AI Insurgent', icon: 'fa-solid fa-cloud', type: 'Venture Profile' },
  arkestro: { title: 'Arkestro', desc: 'Embeds game theory into RFQs, using historical cost and index forecasts to present optimized recommended prices to suppliers.', regulatory: 'SOC 2 Type II Audited Data Security.', stat: 'AI Insurgent', icon: 'fa-solid fa-robot', type: 'Venture Profile' },
  levadata: { title: 'LevaData', desc: 'Cognitive control tower for direct BOMs. Monitors 50+ commodity markets globally and triggers alternate sourcing if constraints arise.', regulatory: 'Enterprise Grade / ISO 27001.', stat: 'Market Defender', icon: 'fa-solid fa-chart-line', type: 'Venture Profile' },
  stem: { title: 'Stem, Inc. (Athena™)', desc: 'Forecasts local grid spikes and dispatches battery storage or signals furnaces to shift cycles to off-peak hours.', regulatory: 'NERC CIP Grid Compliance.', stat: 'AI Insurgent', icon: 'fa-solid fa-bolt', type: 'Venture Profile' },
  citrine: { title: 'Citrine Informatics', desc: 'AI materials informatics platform that dynamically redesigns chemical/alloy formulas to substitute expensive inputs while preserving strength.', regulatory: 'ISO 9001 Material Standards Aligned.', stat: 'AI Insurgent', icon: 'fa-solid fa-vial', type: 'Venture Profile' },
  project44: { title: 'project44', desc: 'High-fidelity multimodal visibility platform predicting terminal gate delays and rail ETAs days in advance.', regulatory: 'GDPR / Rail Integrations.', stat: 'Scale Stage (Unicorn)', icon: 'fa-solid fa-ship', type: 'Venture Profile' },
  flexport: { title: 'Flexport', desc: 'Digital freight forwarding matching and container spot rate bidding networks, reducing custom clearance times.', regulatory: 'C-TPAT / Customs Validated Security.', stat: 'Scale Stage', icon: 'fa-solid fa-plane-departure', type: 'Venture Profile' },
  shippeo: { title: 'Shippeo', desc: 'AI-native real-time transportation visibility (RTTV) providing continuous machine-learning-calculated delivery forecasts.', regulatory: 'GDPR / eCMR Compliant.', stat: 'AI Insurgent', icon: 'fa-solid fa-satellite-dish', type: 'Venture Profile' },
  sennder: { title: 'Sennder', desc: "Europe's leading digital road freight forwarder, aggregating independent trucking carriers into a single carbon-optimized network.", regulatory: 'EU Logistics Data Standards.', stat: 'Scale Stage (Unicorn)', icon: 'fa-solid fa-truck-fast', type: 'Venture Profile' },
  sparetech: { title: 'SPARETECH', desc: 'Automates data normalization, deduplication, and cross-site MRO inventory pooling to reduce excess working capital.', regulatory: 'ERP Integrated / Data Governance.', stat: 'AI Insurgent', icon: 'fa-solid fa-database', type: 'Venture Profile' },
  '4castplus': { title: '4castplus', desc: 'Real-time CapEx project cost tracking, subcontract management, and milestone forecasting for major plant turnarounds.', regulatory: 'Financial Controls / AFE Auditable.', stat: 'Platform Leader', icon: 'fa-solid fa-hard-hat', type: 'Venture Profile' },
  throughput: { title: 'ThroughPut AI', desc: 'Autonomous procurement triggers fueled by physical equipment telemetry and IoT degradation signals.', regulatory: 'IIoT Integrated / SOC 2.', stat: 'AI Insurgent', icon: 'fa-solid fa-microchip', type: 'Venture Profile' },
  ispec: { title: 'iSpec (Remy InfoSource)', desc: 'Secure, unified platform for managing highly complex capital equipment tenders from technical bid to delivery.', regulatory: 'ISO 27001 / Engineering Specs.', stat: 'Specialist', icon: 'fa-solid fa-file-contract', type: 'Vendor Profile' },
  pactum: { title: 'Pactum', desc: 'Autonomous tail-spend negotiation bots leveraging AI to capture hidden operational rebates.', regulatory: 'Enterprise Grade Data Security.', stat: 'AI Insurgent', icon: 'fa-solid fa-robot', type: 'Venture Profile' },
};

/* ─── Tab 3: Capability filter buttons ─── */
const CAP_FILTERS_DEFENSE = [
  { tag: 'carbon-penalties', label: 'CBAM & Carbon Penalties', icon: 'fa-solid fa-smog text-orange-400/80 w-4', hover: 'hover:border-orange-500' },
  { tag: 'geopolitical-risk', label: 'Geopolitical Risks', icon: 'fa-solid fa-earth-americas text-rose-400/80 w-4', hover: 'hover:border-rose-500' },
  { tag: 'plant-downtime', label: 'IIoT Plant Downtime', icon: 'fa-solid fa-screwdriver-wrench text-amber-400/80 w-4', hover: 'hover:border-amber-500' },
  { tag: 'cost-leakage', label: 'Cost Leakage', icon: 'fa-solid fa-money-bill-trend-up text-pink-400/80 w-4', hover: 'hover:border-pink-500' },
  { tag: 'logistical-delay', label: 'Logistical Delays', icon: 'fa-solid fa-truck-ramp-box text-yellow-400/80 w-4', hover: 'hover:border-yellow-500' },
];

const CAP_FILTERS_OFFENSE = [
  { tag: 'agentic-ai', label: 'Agentic AI', icon: 'fa-solid fa-bolt text-indigo-400/80 w-4', hover: 'hover:border-indigo-500' },
  { tag: 'real-time-telemetry', label: 'IIoT & Sensor Telemetry', icon: 'fa-solid fa-chart-line text-cyan-400/80 w-4', hover: 'hover:border-cyan-500' },
  { tag: 'sovereign-control', label: 'Sovereign Control', icon: 'fa-solid fa-shield-halved text-emerald-400/80 w-4', hover: 'hover:border-emerald-500' },
  { tag: 'material-substitution', label: 'GenAI Formulation', icon: 'fa-solid fa-shuffle text-blue-400/80 w-4', hover: 'hover:border-blue-500' },
  { tag: 'green-premiums', label: 'Green Premiums', icon: 'fa-solid fa-leaf text-violet-400/80 w-4', hover: 'hover:border-violet-500' },
];

const MATURITY_FILTERS = [
  { val: 'emerging', label: 'Emerging Capabilities', icon: 'fa-solid fa-seedling text-violet-400/85 w-4', hover: 'hover:border-violet-500' },
  { val: 'growth', label: 'Growth Capabilities', icon: 'fa-solid fa-chart-line text-emerald-400/85 w-4', hover: 'hover:border-emerald-500' },
  { val: 'mature', label: 'Mature Capabilities', icon: 'fa-solid fa-check-double text-blue-400/85 w-4', hover: 'hover:border-blue-500' },
];

/* ─── Tab 3: Capability taxonomy cards ─── */
const TAG_CHIP = {
  'carbon-penalties': { label: 'CBAM Penalties', cls: 'bg-orange-500/10 text-orange-400 border-orange-500/20' },
  'sovereign-control': { label: 'Sovereign Control', cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
  'green-premiums': { label: 'Green Premiums', cls: 'bg-violet-500/10 text-violet-400 border-violet-500/20' },
  'geopolitical-risk': { label: 'Geopolitical Risk', cls: 'bg-rose-500/10 text-rose-400 border-rose-500/20' },
  'material-substitution': { label: 'GenAI Formulation', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  'cost-leakage': { label: 'Cost Leakage', cls: 'bg-pink-500/10 text-pink-400 border-pink-500/20' },
  'real-time-telemetry': { label: 'IIoT Telemetry', cls: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' },
  'agentic-ai': { label: 'Agentic AI', cls: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' },
  'plant-downtime': { label: 'IIoT Downtime', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  'logistical-delay': { label: 'Logistical Delays', cls: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20' },
};

const MATURITY_CHIP = {
  growth: { label: 'Growth', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
  emerging: { label: 'Emerging', cls: 'bg-violet-500/15 text-violet-300 border-violet-500/30' },
  mature: { label: 'Mature', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/30' },
};

const CAP_LAYERS = [
  {
    layer: 4,
    borderL: 'border-l-emerald-500',
    shadow: 'shadow-emerald-500/5',
    numWrap: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    titleCls: 'text-emerald-400',
    title: 'Layer 4: Agentic Compliance & Carbon Arbitrage',
    subtitle: 'Enterprise Defense & Decarbonization Shield',
    cols: 'md:grid-cols-3',
    cards: [
      { title: 'CBAM Borders & Carbon Custom Shields', tags: ['carbon-penalties', 'sovereign-control'], maturity: 'growth', desc: 'Deploying automated customs carbon duty trackers that shield GFG imports from sudden European CBAM custom taxation surges.' },
      { title: 'Scope 3 Emissions Supplier Audits', tags: ['carbon-penalties', 'green-premiums'], maturity: 'growth', desc: 'Structuring absolute Scope 3 tracking across mining partners, proving green lineages to justify and lock down premium prices.' },
      { title: 'Sovereign Critical Mineral Tracking', tags: ['geopolitical-risk', 'sovereign-control'], maturity: 'emerging', desc: 'Tracking specialized alloying inputs (vanadium, nickel) down to primary mines, preventing dependencies on embargoed global supply grids.' },
    ],
  },
  {
    layer: 3,
    borderL: 'border-l-purple-500',
    shadow: 'shadow-purple-500/5',
    numWrap: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
    titleCls: 'text-purple-400',
    title: 'Layer 3: AI-Driven Metallurgical & Energy IBOM',
    subtitle: 'Physical-Digital Ingredient Convergence',
    cols: 'md:grid-cols-3',
    cards: [
      { title: 'Dynamic Blast/EAF Charge Optimization', tags: ['material-substitution', 'cost-leakage'], maturity: 'growth', desc: 'Re-engineering blast furnace charge inputs in real-time, substituting scrap, DRI, or pig iron based on index spot spreads.' },
      { title: 'Algorithmic Energy Load & Grid Hedging', tags: ['real-time-telemetry', 'cost-leakage'], maturity: 'growth', desc: 'Connecting mill electricity consumption loads directly to regional utility grids to run power-heavy phases during low-tariff off-peak periods.' },
      { title: 'Autonomous Tail-Spend Negotiation Agents', tags: ['agentic-ai', 'cost-leakage'], maturity: 'mature', desc: 'Leveraging deep conversation-bots to negotiate long-tail auxiliary steel mill consumables, capturing hidden operational rebates.' },
    ],
  },
  {
    layer: 2,
    borderL: 'border-l-cyan-500',
    shadow: 'shadow-cyan-500/5',
    numWrap: 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400',
    titleCls: 'text-cyan-400',
    title: 'Layer 2: Real-Time Fleet & Logistics Telemetry',
    subtitle: 'Physical Supply Chain Integrity & Flow Velocity',
    cols: 'md:grid-cols-2',
    cards: [
      { title: 'Predictive Multimodal Freight Spot Brokerage', tags: ['logistical-delay', 'real-time-telemetry'], maturity: 'growth', desc: 'Automating spot container bidding across ocean freight liners, keeping delivery rates optimized during severe port gridlocks.' },
      { title: 'Computer-Vision Demurrage & Yard Automation', tags: ['logistical-delay', 'real-time-telemetry'], maturity: 'emerging', desc: 'Tracking rail car and bulk scrap truck dwell times automatically, eliminating costly port demurrage fees and operational warehouse bottlenecks.' },
    ],
  },
  {
    layer: 1,
    borderL: 'border-l-amber-500',
    shadow: 'shadow-amber-500/5',
    numWrap: 'bg-amber-500/10 border-amber-500/20 text-amber-400',
    titleCls: 'text-amber-400',
    title: 'Layer 1: IIoT-Enabled CapEx & MRO',
    subtitle: 'Machinery Sourcing & Facility Preservation',
    cols: '',
    cards: [
      { title: 'Predictive Strategic MRO Procurement', tags: ['plant-downtime', 'real-time-telemetry'], maturity: 'mature', desc: 'Leveraging structural machinery vibration telemetry to trigger automatic strategic bids for high-value critical plant gears, avoiding catastrophic blast furnace shutdowns.' },
    ],
  },
];

const CAP_HOVER = { 4: 'hover:border-emerald-500/40', 3: 'hover:border-purple-500/40', 2: 'hover:border-cyan-500/40', 1: 'hover:border-amber-500/40' };
const CAP_ACTIVE_BORDER = { 4: 'border-emerald-500/60', 3: 'border-purple-500/60', 2: 'border-cyan-500/60', 1: 'border-amber-500/60' };

/* ─── Tab 4: Target Discovery data ─── */
const INSURGENT_BORDER = { emerald: 'border-emerald-500/30', purple: 'border-purple-500/30', cyan: 'border-cyan-500/30', amber: 'border-amber-500/30' };
const BADGE_CLS = {
  emerald: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  purple: 'border-purple-500/30 bg-purple-500/10 text-purple-300',
  cyan: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  amber: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  slate: 'border-slate-700 bg-slate-800 text-slate-400',
};

const DISCOVERY_LAYERS = [
  {
    id: 'L4',
    title: 'Agentic Compliance & Carbon Arbitrage',
    color: 'emerald',
    chipCls: 'bg-emerald-600/20 text-emerald-500 border-emerald-500/30 shadow-emerald-600/10',
    dotCls: 'bg-emerald-500',
    grid: 'grid grid-cols-1 xl:grid-cols-3 gap-5',
    segments: [
      {
        title: '1. CBAM Customs Shield',
        defenders: [
          { name: 'Thomson ONESOURCE', domain: 'thomsonreuters.com', letter: 'T', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'CarbonChain', domain: 'carbonchain.com', letter: 'C', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
          { name: 'carbmee', domain: 'carbmee.com', letter: 'C', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
          { name: 'TSC NetZero', domain: 'tsc.ai', letter: 'T', geo: 'APAC', ownership: 'PRIVATE', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM', badge: 'emerald' },
        ],
      },
      {
        title: '2. Scope 3 Emissions Ledger',
        defenders: [
          { name: 'EcoVadis', domain: 'ecovadis.com', letter: 'E', geo: 'EMEA', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM', badge: 'slate' },
        ],
        insurgents: [
          { name: 'IntegrityNext', domain: 'integritynext.com', letter: 'I', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
          { name: 'Sweep', domain: 'sweep.net', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
        ],
      },
      {
        title: '3. Mineral Geopolitics',
        defenders: [
          { name: 'Exiger', domain: 'exiger.com', letter: 'E', geo: 'AMERICAS', ownership: 'PRIVATE', pedigree: 'AI-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Minespider', domain: 'minespiders.com', letter: 'M', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'emerald' },
        ],
      },
    ],
  },
  {
    id: 'L3',
    title: 'AI-Driven Metallurgical & Energy IBOM',
    color: 'purple',
    chipCls: 'bg-purple-600/20 text-purple-500 border-purple-500/30 shadow-purple-600/10',
    dotCls: 'bg-purple-500',
    grid: 'grid grid-cols-1 xl:grid-cols-3 gap-5',
    segments: [
      {
        title: '4. Dynamic Charge Optimizer',
        defenders: [
          { name: 'LevaData', domain: 'levadata.com', letter: 'L', geo: 'AMERICAS', ownership: 'PRIVATE', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Citrine Informatics', domain: 'citrine.io', letter: 'C', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
        ],
      },
      {
        title: '5. Grid Load Hedging',
        defenders: [
          { name: 'GE Grid Solutions', domain: 'ge.com', letter: 'G', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Stem, Inc. (Athena)', domain: 'stem.com', letter: 'S', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'AI-NATIVE', role: 'PLATFORM' },
          { name: 'AutoGrid', domain: 'auto-grid.com', letter: 'A', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'Metron Energy', domain: 'metron.online', letter: 'M', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'purple' },
        ],
      },
      {
        title: '6. Autonomous Tail Negotiation',
        defenders: [
          { name: 'Coupa Sourcing', domain: 'coupa.com', letter: 'C', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Arkestro', domain: 'arkestro.com', letter: 'A', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'Pactum', domain: 'pactum.com', letter: 'P', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
        ],
      },
    ],
  },
  {
    id: 'L2',
    title: 'Real-Time Fleet & Logistics Telemetry',
    color: 'cyan',
    chipCls: 'bg-cyan-600/20 text-cyan-400 border-cyan-500/30 shadow-cyan-600/10',
    dotCls: 'bg-cyan-500',
    grid: 'grid grid-cols-1 xl:grid-cols-3 gap-5',
    segments: [
      {
        title: '7. Multimodal Spot Freight',
        defenders: [
          { name: 'C.H. Robinson', domain: 'chrobinson.com', letter: 'C', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Flexport', domain: 'flexport.com', letter: 'F', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'Sennder', domain: 'sennder.com', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'cyan' },
        ],
      },
      {
        title: '8. Yard & Demurrage Monitor',
        defenders: [
          { name: 'project44', domain: 'project44.com', letter: 'P', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'Shippeo', domain: 'shippeo.com', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'cyan' },
        ],
      },
    ],
  },
  {
    id: 'L1',
    title: 'IIoT-Enabled CapEx & MRO',
    color: 'amber',
    chipCls: 'bg-amber-600/20 text-amber-400 border-amber-500/30 shadow-amber-600/10',
    dotCls: 'bg-amber-500',
    grid: 'grid grid-cols-1 xl:grid-cols-2 gap-5',
    segments: [
      {
        title: '9. Predictive CapEx & MRO Operations',
        defenders: [
          { name: 'SAP Enterprise MRO', domain: 'sap.com', letter: 'S', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
          { name: 'IBM Maximo', domain: 'ibm.com', letter: 'I', geo: 'AMERICAS', ownership: 'PUBLIC', pedigree: 'LEGACY', role: 'PLATFORM' },
        ],
        insurgents: [
          { name: 'SPARETECH', domain: 'sparetech.io', letter: 'S', geo: 'EMEA', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST', badge: 'amber' },
          { name: '4castplus', domain: '4castplus.com', letter: '4', geo: 'AMERICAS', ownership: 'PRIVATE', pedigree: 'CLOUD-NATIVE', role: 'PLATFORM' },
          { name: 'ThroughPut AI', domain: 'throughput.ai', letter: 'T', geo: 'AMERICAS', ownership: 'VC-BACKED', pedigree: 'AI-NATIVE', role: 'SPECIALIST' },
          { name: 'iSpec', domain: 'remy-ispec.com', letter: 'I', geo: 'APAC', ownership: 'PRIVATE', pedigree: 'CLOUD-NATIVE', role: 'SPECIALIST', badge: 'amber' },
        ],
      },
    ],
  },
];

const DISC_FILTER_GROUPS = [
  { key: 'geo', label: 'Geopolitical Bloc', icon: 'fa-solid fa-globe', options: ['ALL', 'AMERICAS', 'EMEA', 'APAC'], labels: { ALL: 'All', AMERICAS: 'Americas', EMEA: 'EMEA', APAC: 'APAC' } },
  { key: 'ownership', label: 'Ownership Structure', icon: 'fa-solid fa-building', options: ['ALL', 'PUBLIC', 'VC-BACKED'], labels: { ALL: 'All', PUBLIC: 'Public', 'VC-BACKED': 'VC-Backed' } },
  { key: 'pedigree', label: 'Tech Pedigree', icon: 'fa-solid fa-code-branch', options: ['ALL', 'AI-NATIVE', 'LEGACY'], labels: { ALL: 'All', 'AI-NATIVE': 'AI-Native', LEGACY: 'Legacy' } },
  { key: 'role', label: 'Structural Role', icon: 'fa-solid fa-layer-group', options: ['ALL', 'PLATFORM', 'SPECIALIST'], labels: { ALL: 'All', PLATFORM: 'Platform', SPECIALIST: 'Specialist' } },
];

const MAIN_TABS = [
  { id: 1, label: '1. Strategic Context', icon: 'fa-solid fa-compass-drafting' },
  { id: 2, label: '2. Sourcing Stack', icon: 'fa-solid fa-network-wired' },
  { id: 3, label: '3. Interventions', icon: 'fa-solid fa-layer-group' },
  { id: 4, label: '4. Target Discovery', icon: 'fa-solid fa-radar' },
  { id: 5, label: '5. Co-Execution', icon: 'fa-solid fa-infinity' },
];

/* ─── Small render helpers ─── */
const TargetChip = ({ t, layerColor, visible }) => (
  <div
    className={`flex items-center gap-2.5 p-2 px-3 bg-[#191e2e] border ${t.type === 'insurgent' ? INSURGENT_BORDER[layerColor] : 'border-[#2a324a]'} rounded-xl transition-all`}
    style={{ display: visible ? 'flex' : 'none' }}
  >
    <img
      src={favicon(t.domain)}
      alt=""
      className="w-5 h-5 rounded bg-white p-0.5"
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placehold(t.letter); }}
    />
    <span className="text-xs font-semibold text-slate-200">{t.name}</span>
    {t.badge && <span className={`text-[8px] px-1.5 py-0.5 rounded border ${BADGE_CLS[t.badge]}`}>{t.geo}</span>}
  </div>
);

const PlayerChip = ({ playerKey, domain, letter, name, onSelect }) => (
  <span
    onClick={(e) => onSelect(e, playerKey)}
    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-950 hover:bg-slate-800 text-[11px] font-medium text-slate-200 border border-slate-800 rounded-lg transition-all"
  >
    <img
      src={favicon(domain, 32)}
      alt=""
      className="w-3.5 h-3.5 rounded"
      onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = placehold(letter, '16x16'); }}
    />
    {name}
  </span>
);

export default function HeavyIndustriesSourcing() {
  /* Cover / app transition */
  const [coverVisible, setCoverVisible] = useState(true);
  const [coverOpaque, setCoverOpaque] = useState(true);
  const [appVisible, setAppVisible] = useState(false);
  const [appOpaque, setAppOpaque] = useState(false);

  /* Tabs */
  const [activeTab, setActiveTab] = useState(1);

  /* Tab 2: layer / player detail (defaults to Layer 4 per window.onload) */
  const [selectedLayer, setSelectedLayer] = useState(4);
  const [detail, setDetail] = useState(LAYERS[4]);
  const [detailMode, setDetailMode] = useState('layer'); // 'layer' | 'player'

  /* Tab 3: capability filters */
  const [capFilter, setCapFilter] = useState('ALL');
  const [maturityFilter, setMaturityFilter] = useState('ALL');

  /* Tab 4: discovery filters */
  const [discFilters, setDiscFilters] = useState({ geo: 'ALL', ownership: 'ALL', pedigree: 'ALL', role: 'ALL' });

  const launchEngine = () => {
    setCoverOpaque(false);
    setTimeout(() => {
      setCoverVisible(false);
      setAppVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setAppOpaque(true)));
    }, 1000);
  };

  const returnToCover = () => {
    setAppOpaque(false);
    setTimeout(() => {
      setAppVisible(false);
      setCoverVisible(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setCoverOpaque(true)));
    }, 1000);
  };

  const selectLayer = (id) => {
    setSelectedLayer(id);
    setDetail(LAYERS[id]);
    setDetailMode('layer');
  };

  const selectPlayer = (e, key) => {
    if (e) e.stopPropagation();
    setDetail(PLAYERS[key]);
    setDetailMode('player');
  };

  const toggleCapFilter = (tag) => setCapFilter((cur) => (cur === tag ? 'ALL' : tag));
  const toggleMaturityFilter = (val) => setMaturityFilter((cur) => (cur === val ? 'ALL' : val));
  const setDiscFilter = (category, val) => setDiscFilters((f) => ({ ...f, [category]: val }));

  const matchTarget = (t) =>
    (discFilters.geo === 'ALL' || t.geo === discFilters.geo) &&
    (discFilters.ownership === 'ALL' || t.ownership === discFilters.ownership) &&
    (discFilters.pedigree === 'ALL' || t.pedigree === discFilters.pedigree) &&
    (discFilters.role === 'ALL' || t.role === discFilters.role);

  const matchCount = useMemo(() => {
    let count = 0;
    DISCOVERY_LAYERS.forEach((l) =>
      l.segments.forEach((s) => {
        s.defenders.forEach((t) => { if (matchTarget(t)) count++; });
        s.insurgents.forEach((t) => { if (matchTarget(t)) count++; });
      })
    );
    return count;
  }, [discFilters]);

  const capCardClass = (card, layer) => {
    const matches =
      (capFilter === 'ALL' || card.tags.includes(capFilter)) &&
      (maturityFilter === 'ALL' || card.maturity === maturityFilter);
    const narrowed = capFilter !== 'ALL' || maturityFilter !== 'ALL';
    const border = matches && narrowed ? CAP_ACTIVE_BORDER[layer] : 'border-slate-800/60';
    const opacity = matches ? 'opacity-100' : 'opacity-20';
    return `cap-card bg-slate-950/80 p-6 border ${border} ${opacity} rounded-xl ${CAP_HOVER[layer]} transition-all duration-300 group flex flex-col`;
  };

  const layerBoxClass = (id) => {
    if (selectedLayer === id) {
      const accent = { 1: 'border-amber-500 shadow-amber-500/10', 2: 'border-cyan-500 shadow-cyan-500/10', 3: 'border-purple-500 shadow-purple-500/10', 4: 'border-emerald-500 shadow-emerald-500/10' }[id];
      return `group bg-slate-900 border-2 ${accent} rounded-xl p-5 cursor-pointer transition-all-300 translate-x-1 relative overflow-hidden shadow-lg`;
    }
    return `group bg-slate-900 border border-slate-800 rounded-xl p-5 cursor-pointer transition-all-300 hover:border-amber-500/40 hover:translate-x-1 relative overflow-hidden`;
  };

  const tabBtnClass = (id) => {
    const base = 'px-4 py-4 rounded-xl text-[11px] sm:text-xs tracking-wider uppercase transition-all-300 flex items-center justify-center gap-2';
    const span5 = id === 5 ? 'col-span-2 lg:col-span-1 ' : '';
    if (activeTab === id) {
      return `${span5}${base} font-extrabold bg-amber-500/10 border-2 border-amber-500 text-amber-400 shadow-lg shadow-amber-500/5`;
    }
    return `${span5}${base} font-bold bg-slate-900/40 border border-slate-800/60 text-slate-400 hover:text-white`;
  };

  const sectionClass = (id) => {
    if (activeTab !== id) return 'hidden';
    return id === 4 ? 'space-y-6 block animate-fade-in' : 'space-y-8 block animate-fade-in';
  };

  const capFilterBtnClass = (f) => {
    if (capFilter === f.tag) {
      return 'btn-cap-filter flex items-center w-full justify-start gap-2.5 px-3.5 py-2.5 bg-emerald-500/15 border-2 border-emerald-500 text-emerald-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-emerald-500/5';
    }
    return `btn-cap-filter flex items-center w-full justify-start gap-2.5 px-3.5 py-2.5 bg-zinc-950 border border-zinc-800 ${f.hover} text-zinc-400 hover:text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all`;
  };

  const maturityFilterBtnClass = (f) => {
    if (maturityFilter === f.val) {
      return 'btn-maturity-filter flex items-center w-full justify-start gap-2.5 px-3.5 py-2.5 bg-violet-500/15 border-2 border-violet-500 text-violet-400 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all shadow-lg shadow-violet-500/5';
    }
    return `btn-maturity-filter flex items-center w-full justify-start gap-2.5 px-3.5 py-2.5 bg-zinc-950 border border-zinc-800 ${f.hover} text-zinc-400 hover:text-white text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all`;
  };

  const discBtnClass = (category, val) => {
    const base = `btn-filter-${category} px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-wider rounded-md transition-all`;
    if (discFilters[category] === val) return `${base} bg-slate-800 text-white border border-slate-600`;
    return `${base} text-slate-400 border border-transparent hover:text-slate-200`;
  };

  return (
    <div className="bg-slate-950 text-slate-100 antialiased min-h-screen selection:bg-amber-500 selection:text-black" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Fonts & icon stylesheets (hoisted to <head> by React 19) */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" precedence="default" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" precedence="default" />

      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #030712; }
        ::-webkit-scrollbar-thumb { background: #1f2937; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #374151; }
        .glow-amber { box-shadow: 0 0 25px -5px rgba(245, 158, 11, 0.15); }
        .glow-cyan { box-shadow: 0 0 25px -5px rgba(6, 182, 212, 0.15); }
        .glow-purple { box-shadow: 0 0 25px -5px rgba(168, 85, 247, 0.15); }
        .glow-emerald { box-shadow: 0 0 25px -5px rgba(16, 185, 129, 0.15); }
        .transition-all-300 { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-in { animation: fadeIn 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
      `}</style>

      {/* ==================== COVER PAGE (LAUNCH SCREEN) ==================== */}
      <div
        className={`fixed inset-0 z-[100] bg-slate-950 flex-col items-center justify-center transition-opacity duration-1000 ${coverVisible ? 'flex' : 'hidden'} ${coverOpaque ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        {/* Ambient Background Glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-tr from-cyan-500/10 via-blue-500/5 to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative z-10 text-center space-y-10 px-4 max-w-4xl mx-auto flex flex-col items-center">
          {/* DJA Frontier Brand Icon */}
          <div className="w-20 h-20 bg-slate-900 border border-slate-800 rounded-2xl flex items-center justify-center shadow-2xl relative overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 to-purple-600/10 opacity-50"></div>
            <i className="fa-solid fa-industry text-4xl text-transparent bg-clip-text bg-gradient-to-tr from-cyan-400 via-blue-500 to-purple-500 relative z-10"></i>
          </div>

          {/* Core Typography */}
          <div className="space-y-6">
            <span className="text-[10px] font-bold uppercase tracking-[0.4em] text-slate-500 block">Confidential Boardroom Briefing</span>

            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight text-white leading-tight">
              Procurement Transformation <br className="hidden lg:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">Alpha Engine</span>
            </h1>

            <p className="text-sm sm:text-base text-slate-400 font-medium tracking-wide max-w-2xl mx-auto leading-relaxed">
              Metals, Mining, and Energy Value-Creation Architecture. <br className="hidden sm:block" />A unified dashboard built on GFG Alliance parameters for margin preservation, dynamic charge optimization, and carbon compliance.
            </p>
          </div>

          {/* Launch Action */}
          <div className="pt-8">
            <button
              onClick={launchEngine}
              className="group relative px-8 py-4 bg-slate-950/50 border border-slate-700 hover:border-cyan-500/50 rounded-full overflow-hidden transition-all duration-500 focus:outline-none shadow-[0_0_40px_-10px_rgba(6,182,212,0.1)] hover:shadow-[0_0_40px_-10px_rgba(6,182,212,0.3)]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 to-purple-500/10 translate-y-[100%] group-hover:translate-y-0 transition-transform duration-500 ease-out"></div>
              <span className="relative z-10 flex items-center gap-3 text-xs font-bold text-slate-300 group-hover:text-white uppercase tracking-[0.2em] transition-colors">
                Initialize System <i className="fa-solid fa-fingerprint text-cyan-400 group-hover:text-purple-400 transition-colors"></i>
              </span>
            </button>
          </div>
        </div>

        {/* Corporate Meta Footer */}
        <div className="absolute bottom-8 left-0 right-0 text-center text-[9px] text-slate-600 font-mono tracking-widest uppercase flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4">
          <span>DJA Frontier IP</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-slate-700"></span>
          <span>June 2026</span>
          <span className="hidden sm:block w-1 h-1 rounded-full bg-slate-700"></span>
          <span>Abu Dhabi / Luxembourg</span>
        </div>
      </div>

      {/* ==================== MAIN APPLICATION CONTAINER ==================== */}
      <div className={`${appVisible ? '' : 'hidden'} ${appOpaque ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-1000 pb-20`}>
        <header className="border-b border-slate-800 bg-slate-900/40 backdrop-blur-md sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Clickable Logo/Branding leading back to cover */}
            <div className="flex items-center gap-4 cursor-pointer group" onClick={returnToCover} title="Return to Launch Screen">
              <div className="bg-gradient-to-tr from-cyan-400 via-blue-500 to-purple-600 p-2.5 rounded-xl shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-all duration-300">
                <i className="fa-solid fa-industry text-white text-xl group-hover:scale-110 transition-transform duration-300"></i>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-widest text-cyan-400">Metals, Mining & Energy Platform</span>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight text-white group-hover:text-slate-200 transition-colors duration-300">
                  DJA Frontier <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">Decision Engine</span>
                </h1>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 space-y-8">
          {/* Master Presentation Tabs Bar */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 border-b border-slate-800/80 pb-4">
            {MAIN_TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={tabBtnClass(tab.id)}>
                <i className={`${tab.icon} text-sm`}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* ==================== TAB 1 CONTENT: STRATEGIC CONTEXT ==================== */}
          <section className={sectionClass(1)}>
            {/* Executive Summary Header */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center border-b border-slate-800/80 pb-8">
              <div className="lg:col-span-2 space-y-4">
                <span className="px-3 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-full text-xs font-bold tracking-wider uppercase">Value-Creation Alignment</span>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                  Securing Margins & Accelerating Decarbonization in GFG Industrial Ecosystems
                </h2>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                  In heavy industries like steel, mining, and power generation, you do not set the global commodity price. You are price-takers. Procurement and sourcing define <strong>EBITDA protection, raw material security, and green compliance.</strong> When production depends on volatile electric grids, blast furnace charges, and N-tier carbon custom taxes, sourcing defines the enterprise valuation itself.
                </p>
              </div>

              {/* GFG Alliance Mandate Box */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl space-y-2 glow-amber">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">Boardroom Mandate</h3>
                <p className="text-xs text-slate-300 leading-relaxed italic">
                  "EAF (Electric Arc Furnace) and green hydrogen transformations compress carbon footprints, but if grid-load planning, scrap metals, or CBAM tracking suffer from operational latency, your structural premium is lost."
                </p>
              </div>
            </div>

            {/* THE ALPHA THESIS: Upstream vs Downstream */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 p-6 sm:p-8 rounded-2xl relative overflow-hidden glow-cyan">
              <div className="absolute -top-20 -right-20 w-48 h-48 bg-cyan-500/10 rounded-full blur-3xl"></div>

              <div className="flex flex-col sm:flex-row gap-6 sm:items-start relative z-10">
                <div className="sm:w-1/3 space-y-3">
                  <span className="px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 rounded-full text-[9px] font-bold tracking-wider uppercase">The DJA Frontier Thesis</span>
                  <h3 className="text-xl sm:text-2xl font-black text-white tracking-tight leading-tight">Value is Upstream, Not Downstream</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    Traditional procurement focuses on tactical execution (P2P workflows, manual purchase order routes). We intentionally bypass back-office software to prioritize <strong>upstream strategic sourcing, raw material hedges, and green supply networks.</strong>
                  </p>
                </div>

                <div className="sm:w-2/3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Downstream */}
                  <div className="bg-slate-950/60 border border-slate-800/80 rounded-xl p-5 flex flex-col gap-3 opacity-60 hover:opacity-100 transition-opacity duration-300">
                    <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400">
                      <i className="fa-solid fa-file-invoice"></i>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-slate-300 uppercase tracking-widest mb-1.5">The Downstream Illusion</h4>
                      <p className="text-[11px] text-slate-400 leading-relaxed">Saving minor percentages on administrative invoice processing yields nominal savings but has zero impact on structural margin protection during commodity crashes.</p>
                    </div>
                  </div>

                  {/* Upstream */}
                  <div className="bg-slate-900 border border-cyan-500/40 rounded-xl p-5 flex flex-col gap-3 shadow-[0_0_20px_rgba(6,182,212,0.1)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-cyan-500/10 rounded-bl-full"></div>
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-400 flex items-center justify-center">
                      <i className="fa-solid fa-bolt"></i>
                    </div>
                    <div className="relative z-10">
                      <h4 className="text-xs font-bold text-cyan-400 uppercase tracking-widest mb-1.5">The Upstream Reality</h4>
                      <p className="text-[11px] text-slate-300 leading-relaxed">True alpha is generated upstream. Re-engineering raw furnace charges, hedging spot-energy, and securing CBAM-validated supply lines shields GFG Alliance assets from market shocks.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Shifting Value Frontiers Layout */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-amber-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">Strategic Context: Shifting Value Frontiers</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Shift 1 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-amber-400 uppercase tracking-widest block">Strategic Shift 1</span>
                    <h4 className="text-base font-black text-white">The Volatile Energy Input Dynamic</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Electric Arc Furnace operations require continuous power grid inputs. Sourcing must shift from fixed monthly billing to <strong>real-time spot-market load hedging</strong> to prevent running massive, high-power mill cycles during pricing spikes.
                    </p>
                  </div>

                  {/* Energy load representation */}
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800/60 text-[10px] space-y-3 font-mono mt-4">
                    <div className="space-y-1">
                      <div className="flex justify-between text-[8px] text-slate-500">
                        <span>PEAK SHAVING OPTIMIZATION</span>
                        <span className="text-amber-400 font-bold">12% COGS SAVINGS</span>
                      </div>
                      <div className="w-full bg-slate-900 rounded-full h-1">
                        <div className="bg-amber-500 h-1 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Shift 2 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-cyan-400 uppercase tracking-widest block">Strategic Shift 2</span>
                    <h4 className="text-base font-black text-white">The Dynamic Metallurgical "IBOM"</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Blast furnace and EAF recipes (scrap, pig iron, coke, limestone) cannot remain static. AI must dynamically re-calculate the <strong>Industrial Bill of Materials (IBOM)</strong> based on daily scrap grade pricing and alloy availability to preserve product margin.
                    </p>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60 flex items-center justify-between text-[10px] text-slate-400 mt-4">
                    <span><i className="fa-solid fa-calculator text-cyan-400 mr-1.5 animate-pulse"></i> Dynamic Charge Recalibration</span>
                    <span className="font-bold text-cyan-400 font-mono">Real-Time</span>
                  </div>
                </div>

                {/* Shift 3 Card */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 relative overflow-hidden flex flex-col justify-between">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl"></div>
                  <div className="space-y-3">
                    <span className="text-[9px] font-bold text-purple-400 uppercase tracking-widest block">Strategic Shift 3</span>
                    <h4 className="text-base font-black text-white">Green steel Premium & CBAM Borders</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      Sourcing carbon-heavy steel or raw minerals across regulated borders is triggering severe CBAM custom penalties. Sourcing teams must audit Scope 3 supplier carbon data to verify clean lineage and back up the <strong>GREENSTEEL premium.</strong>
                    </p>
                  </div>
                  <div className="bg-slate-950 p-3 rounded-xl border border-slate-800/60 flex items-center justify-between text-[10px] text-slate-400 mt-4">
                    <span><i className="fa-solid fa-leaf text-purple-400 mr-1.5"></i> CBAM Compliance Verification</span>
                    <span className="font-bold text-purple-400 font-mono">100% Audited</span>
                  </div>
                </div>
              </div>
            </div>

            {/* THE EBITDA CATALYST: 5 CONVERGED TECHNOLOGIES */}
            <div className="pt-8 border-t border-slate-800/80 space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-indigo-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">The EBITDA Catalyst: 5 Converged Technologies Re-Engineering the Margin</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {/* Tech 1 */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-emerald-500/40 transition-all-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center shrink-0"><i className="fa-solid fa-network-wired"></i></div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider leading-snug">Deep-Tier Traceability & Carbon Graphs</h4>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed"><strong className="text-slate-200">The Regulatory Lever:</strong> Moving from inflated default estimates to real-time carbon data removes the standard 30% "default custom surcharge penalty" at European borders.</p>
                </div>
                {/* Tech 2 */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-purple-500/40 transition-all-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-400 flex items-center justify-center shrink-0"><i className="fa-solid fa-vial-circle-check"></i></div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider leading-snug">Generative Materials Informatics</h4>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed"><strong className="text-slate-200">The Material Lever:</strong> Deep learning dynamic systems substitute volatile alloying elements on-the-fly while mathematically ensuring 100% structural product strength.</p>
                </div>
                {/* Tech 3 */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-amber-500/40 transition-all-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center shrink-0"><i className="fa-solid fa-bolt"></i></div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider leading-snug">Algorithmic Grid Peak Arbitrage</h4>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed"><strong className="text-slate-200">The Energy Lever:</strong> Predictive AI transforms high-power EAF melting cycles from a massive utility expense into a flexible, automated spot-market load-hedging asset.</p>
                </div>
                {/* Tech 4 */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-cyan-500/40 transition-all-300">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center shrink-0"><i className="fa-solid fa-train-subway"></i></div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider leading-snug">Multi-Modal Maritime & Rail Telemetry</h4>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed"><strong className="text-slate-200">The Logistics Lever:</strong> High-fidelity carrier tracking predicts port and terminal bottlenecks days in advance, stripping out millions in shipping delays and demurrage penalties.</p>
                </div>
                {/* Tech 5 */}
                <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-5 hover:border-indigo-500/40 transition-all-300 md:col-span-2 lg:col-span-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center shrink-0"><i className="fa-solid fa-database"></i></div>
                    <h4 className="text-xs font-bold text-white uppercase tracking-wider leading-snug">Semantic Big Data & IIoT Predictive MRO</h4>
                  </div>
                  <p className="text-[11px] text-slate-400 leading-relaxed"><strong className="text-slate-200">The Asset Lever:</strong> Unifying chaotic inventory catalogs unlocks capital pooling, while machine vision and IoT triggers strategic parts orders to prevent sudden mill shutdowns.</p>
                </div>
              </div>
            </div>

            {/* Strategic Boardroom Recommendations */}
            <div className="pt-8 border-t border-slate-800/80 space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-emerald-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">Strategic Boardroom Recommendations</h3>
              </div>

              <div className="space-y-4">
                {/* Rec 1 */}
                <div className="bg-slate-900/40 border-l-4 border-l-amber-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(245,158,11,0.15)]">
                    <i className="fa-solid fa-bolt"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Implement Spot-Energy Grid Sourcing Integration</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Integrate mill plant operations directly with dynamic electricity and gas spot markets. Shifting high-energy furnace cycles to non-peak hours protects margins from power market volatility.
                    </p>
                  </div>
                </div>

                {/* Rec 2 */}
                <div className="bg-slate-900/40 border-l-4 border-l-cyan-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(6,182,212,0.15)]">
                    <i className="fa-solid fa-vial-reconciliation"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Transition to Algorithmic IBOM Re-calculators</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Scrap static raw material ordering recipes. Deploy dynamic software models that adjust furnace inputs hourly based on raw iron ore, coke, and scrap metal spot arbitrage indices.
                    </p>
                  </div>
                </div>

                {/* Rec 3 */}
                <div className="bg-slate-900/40 border-l-4 border-l-purple-500 border border-slate-800 rounded-r-2xl p-6 flex flex-col md:flex-row gap-5 md:items-center hover:bg-slate-800/40 transition-all-300">
                  <div className="shrink-0 w-12 h-12 rounded-full bg-purple-500/10 border border-purple-500/30 text-purple-400 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(168,85,247,0.15)]">
                    <i className="fa-solid fa-shield-halved"></i>
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1.5">Insulate margins via CBAM Carbon Audits</h4>
                    <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                      Establish complete Scope 3 carbon tracing across international mineral mining sites. Verifiable carbon tracking converts compliance into a premium GREENSTEEL valuation asset.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Transition Call-To-Action */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Strategic Paradigm Clear? Ready to Map GFG Supply Chains?</h4>
                <p className="text-xs text-slate-400">Pivot to the next section to review our 4-layer heavy industrial architecture.</p>
              </div>
              <button
                onClick={() => setActiveTab(2)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2"
              >
                View the Sourcing Stack <i className="fa-solid fa-circle-arrow-right"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 2 CONTENT: SOURCING STACK (THE "WHAT") ==================== */}
          <section className={sectionClass(2)}>
            {/* Market Map Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Left Column: The 4 Layers */}
              <div className="lg:col-span-8 space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">
                    <i className="fa-solid fa-network-wired text-amber-400"></i>
                    Sourcing Stack Architecture
                  </h3>
                  <span className="text-xs text-slate-500">Click a Layer or Player to inspect parameters</span>
                </div>

                {/* LAYER 4 */}
                <div onClick={() => selectLayer(4)} className={layerBoxClass(4)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-emerald-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 font-extrabold rounded-lg text-sm">4</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-emerald-400 transition-all-300 uppercase tracking-wider">Agentic Compliance & Carbon Arbitrage</h4>
                        <span className="text-[10px] text-slate-500 font-medium">CBAM Customs & Scope 3 Tracing</span>
                      </div>
                    </div>

                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Blind carbon purchases leading to severe custom penalties</span>
                      <span className="text-emerald-400 block mt-1"><i className="fa-solid fa-circle-check text-emerald-500 mr-1"></i> Verified Scope 3 emission footprints & green compliance shields</span>
                    </div>

                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="exiger" domain="exiger.com" letter="E" name="Exiger" onSelect={selectPlayer} />
                        <PlayerChip playerKey="carbonchain" domain="carbonchain.com" letter="C" name="CarbonChain" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 3 */}
                <div onClick={() => selectLayer(3)} className={layerBoxClass(3)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-purple-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-purple-500/10 border border-purple-500/20 text-purple-400 font-extrabold rounded-lg text-sm">3</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-purple-400 transition-all-300 uppercase tracking-wider">AI-Driven Metallurgical & Energy IBOM</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Algorithmic Recipe Hedging</span>
                      </div>
                    </div>

                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Static furnace raw charges and fixed energy billing</span>
                      <span className="text-purple-400 block mt-1"><i className="fa-solid fa-circle-check text-purple-500 mr-1"></i> Dynamic recipe optimization with spot power load shaving</span>
                    </div>

                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="arkestro" domain="arkestro.com" letter="A" name="Arkestro" onSelect={selectPlayer} />
                        <PlayerChip playerKey="pactum" domain="pactum.com" letter="P" name="Pactum" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 2 */}
                <div onClick={() => selectLayer(2)} className={layerBoxClass(2)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-cyan-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-extrabold rounded-lg text-sm">2</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-cyan-400 transition-all-300 uppercase tracking-wider">Real-Time Fleet & Logistics Telemetry</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Ocean, Rail & Yard Telemetry</span>
                      </div>
                    </div>

                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Static sea shipping allocations with demurrage spikes</span>
                      <span className="text-cyan-400 block mt-1"><i className="fa-solid fa-circle-check text-cyan-500 mr-1"></i> Dynamic cargo routing, predictive booking & yard tracking</span>
                    </div>

                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="project44" domain="project44.com" letter="P" name="project44" onSelect={selectPlayer} />
                        <PlayerChip playerKey="flexport" domain="flexport.com" letter="F" name="Flexport" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* LAYER 1 */}
                <div onClick={() => selectLayer(1)} className={layerBoxClass(1)}>
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-amber-500"></div>
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                    <div className="sm:col-span-3 flex items-center gap-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-amber-500/10 border border-amber-500/20 text-amber-400 font-extrabold rounded-lg text-sm">1</span>
                      <div>
                        <h4 className="text-xs font-black text-white group-hover:text-amber-400 transition-all-300 uppercase tracking-wider">IIoT-Enabled CapEx & MRO</h4>
                        <span className="text-[10px] text-slate-500 font-medium">Plant Downtime Prevention</span>
                      </div>
                    </div>

                    <div className="sm:col-span-4 text-xs">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Operational Shift</span>
                      <span className="text-slate-400 block"><i className="fa-solid fa-triangle-exclamation text-rose-500/70 mr-1"></i> Reactive machinery downtime with emergency margins</span>
                      <span className="text-amber-400 block mt-1"><i className="fa-solid fa-circle-check text-amber-500 mr-1"></i> AI-integrated global spares & predictive part sourcing</span>
                    </div>

                    <div className="sm:col-span-5">
                      <span className="text-[9px] font-extrabold uppercase tracking-widest text-slate-500 block mb-1">Representative Players</span>
                      <div className="flex flex-wrap gap-2">
                        <PlayerChip playerKey="sap" domain="sap.com" letter="S" name="SAP Enterprise" onSelect={selectPlayer} />
                        <PlayerChip playerKey="sparetech" domain="sparetech.io" letter="S" name="SPARETECH" onSelect={selectPlayer} />
                        <PlayerChip playerKey="4castplus" domain="4castplus.com" letter="4" name="4castplus" onSelect={selectPlayer} />
                        <PlayerChip playerKey="throughput" domain="throughput.ai" letter="T" name="ThroughPut AI" onSelect={selectPlayer} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Detail Sidebar Panel */}
              <div className="lg:col-span-4 bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-6 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 text-amber-400 border-b border-slate-800 pb-3 mb-4">
                    <i className={`${detail.icon} text-lg text-amber-400`}></i>
                    <h3 className="text-xs font-bold uppercase tracking-wider">{detail.type}</h3>
                  </div>

                  {/* Detail Texts */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-extrabold text-white">{detail.title}</h4>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{detail.desc}</p>
                    </div>

                    {/* Validation Audit Box */}
                    <div className="bg-slate-950 p-3.5 border border-slate-800 rounded-lg space-y-1">
                      <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Strategic Pathway & Compliance Profile</span>
                      <p className="text-xs text-slate-300 leading-relaxed">{detail.regulatory}</p>
                    </div>

                    {/* Tech Moat Boxes (shown for Layers, hidden for individual players) */}
                    <div className="space-y-4" style={{ display: detailMode === 'layer' ? 'block' : 'none' }}>
                      <div className="bg-slate-950/50 p-3.5 border border-slate-800/80 rounded-lg space-y-1">
                        <span className="text-[9px] font-bold text-indigo-400 uppercase tracking-wider block">Underlying Technological Moat</span>
                        <p className="text-xs text-white font-medium leading-relaxed">{detail.moat}</p>
                      </div>
                      <div className="bg-slate-950/50 p-3.5 border border-slate-800/80 rounded-lg space-y-1">
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider block">Boardroom Context & Due Diligence</span>
                        <p className="text-xs text-slate-400 leading-relaxed">{detail.context}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Strategic Target Rating */}
                <div className="bg-amber-500/10 border border-amber-500/20 p-4 rounded-xl space-y-1.5">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-amber-400 block">Sourcing Margin Metric</span>
                  <div className="text-sm font-black text-white">{detail.stat}</div>
                </div>
              </div>
            </div>

            {/* Transition Call-To-Action to Tab 3 */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Understand the Sourcing Architecture? Let's Define the Capabilities.</h4>
                <p className="text-xs text-slate-400">Move directly to the capability taxonomy to review unboxed core operational and regulatory requirements by layer.</p>
              </div>
              <button
                onClick={() => setActiveTab(3)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                Define Strategic Interventions <i className="fa-solid fa-layer-group"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 3 CONTENT: CAPABILITY TAXONOMY (THE DEFINITIONS) ==================== */}
          <section className={sectionClass(3)}>
            <div className="space-y-4 border-b border-zinc-800/80 pb-6">
              <span className="px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-full text-xs font-bold tracking-wider uppercase">Target Operating Model</span>
              <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                Heavy Industry Sourcing Interventions & Maturity Roadmap
              </h2>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed max-w-4xl">
                Define your Target Operating Model before evaluating specific vendors. This roadmap isolates the nine core interventions required to secure and accelerate GFG industrial supply chains. Use the lenses below to instantly filter by defensive pain points, offensive innovation vectors, and deployment maturity.
              </p>
            </div>

            {/* Interactive Strategic Thesis Filters */}
            <div className="bg-zinc-900 border border-zinc-800 p-5 rounded-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 md:divide-x md:divide-zinc-800/60">
                {/* 1. Pain Point Filters (Defense) */}
                <div className="space-y-3 md:pr-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-triangle-exclamation text-rose-500/70"></i> Defense Lenses</span>
                    <button onClick={() => setCapFilter('ALL')} className="text-[9px] font-bold text-zinc-600 hover:text-rose-400 uppercase tracking-wider transition-colors flex items-center gap-1" title="Clear Defense Filters"><i className="fa-solid fa-arrow-rotate-left"></i> Reset</button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {CAP_FILTERS_DEFENSE.map((f) => (
                      <button key={f.tag} onClick={() => toggleCapFilter(f.tag)} className={capFilterBtnClass(f)}>
                        <i className={f.icon}></i> {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 2. Innovation Vector Filters (Offense) */}
                <div className="space-y-3 md:px-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-bolt text-indigo-400/80"></i> Offense Vectors</span>
                    <button onClick={() => setCapFilter('ALL')} className="text-[9px] font-bold text-zinc-600 hover:text-indigo-400 uppercase tracking-wider transition-colors flex items-center gap-1" title="Clear Offense Filters"><i className="fa-solid fa-arrow-rotate-left"></i> Reset</button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {CAP_FILTERS_OFFENSE.map((f) => (
                      <button key={f.tag} onClick={() => toggleCapFilter(f.tag)} className={capFilterBtnClass(f)}>
                        <i className={f.icon}></i> {f.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 3. Maturity Phase Filters */}
                <div className="space-y-3 md:pl-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1.5"><i className="fa-solid fa-chart-bar text-zinc-600"></i> Maturity Lens</span>
                    <button onClick={() => setMaturityFilter('ALL')} className="text-[9px] font-bold text-zinc-600 hover:text-violet-400 uppercase tracking-wider transition-colors flex items-center gap-1" title="Clear Maturity Filter"><i className="fa-solid fa-arrow-rotate-left"></i> Reset</button>
                  </div>
                  <div className="flex flex-col gap-2">
                    {MATURITY_FILTERS.map((f) => (
                      <button key={f.val} onClick={() => toggleMaturityFilter(f.val)} className={maturityFilterBtnClass(f)}>
                        <i className={f.icon}></i> {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Capability Taxonomy Layers */}
            <div className="space-y-6">
              {CAP_LAYERS.map((cl) => (
                <div key={cl.layer} className={`bg-slate-900 border border-slate-800/80 rounded-2xl p-6 relative overflow-hidden border-l-4 ${cl.borderL} shadow-lg ${cl.shadow}`}>
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg border ${cl.numWrap} flex items-center justify-center text-sm font-bold shadow-inner shrink-0`}>{cl.layer}</div>
                      <div>
                        <h3 className={`text-sm font-black uppercase tracking-wider ${cl.titleCls}`}>{cl.title}</h3>
                        <span className="text-[10px] text-slate-500 font-semibold block uppercase">{cl.subtitle}</span>
                      </div>
                    </div>
                  </div>

                  {/* Strategic Interventions Grid */}
                  <div className={`grid grid-cols-1 ${cl.cols} gap-5`}>
                    {cl.cards.map((card) => (
                      <div key={card.title} className={capCardClass(card, cl.layer)}>
                        <h4 className="text-sm font-bold text-slate-200 group-hover:text-white tracking-wide mb-3 leading-snug">{card.title}</h4>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {card.tags.map((tag) => (
                            <span key={tag} className={`px-2 py-1 rounded text-[9px] font-bold ${TAG_CHIP[tag].cls} border uppercase tracking-widest`}>{TAG_CHIP[tag].label}</span>
                          ))}
                          <span className={`px-2 py-1 rounded text-[9px] font-bold ${MATURITY_CHIP[card.maturity].cls} border uppercase tracking-widest font-mono ml-auto`}>{MATURITY_CHIP[card.maturity].label}</span>
                        </div>
                        <p className="text-xs text-slate-400 leading-relaxed mt-auto">{card.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Transition Call-To-Action to Tab 4 */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Capabilities Aligned? Let's Locate the Market Providers.</h4>
                <p className="text-xs text-slate-400">Advance to the Target Discovery Engine to screen Insurgents vs. Defenders across our GFG supply network.</p>
              </div>
              <button
                onClick={() => setActiveTab(4)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                Launch Target Discovery <i className="fa-solid fa-radar"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 4 CONTENT: TARGET DISCOVERY ENGINE ==================== */}
          <section className={sectionClass(4)}>
            {/* Header */}
            <div>
              <h2 className="text-2xl font-black tracking-tight text-white flex items-center gap-3">
                <i className="fa-solid fa-crosshairs text-amber-500"></i> Target Discovery Engine
              </h2>
              <p className="text-slate-400 text-sm mt-1">Use GFG firmographic and strategic filters to isolate specific M&A targets, competitive threats, and high-growth disruptors within the heavy industrial sourcing ecosystem.</p>
            </div>

            {/* Master Filter Panel */}
            <div className="bg-[#111424] border border-[#23283b] rounded-2xl p-5 space-y-6">
              {/* Firmographic Selectors */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {DISC_FILTER_GROUPS.map((g) => (
                  <div key={g.key} className="space-y-3">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest flex items-center gap-1.5"><i className={`${g.icon} text-slate-600`}></i> {g.label}</span>
                    <div className="flex flex-wrap gap-1.5 bg-[#0b0e17] p-1.5 rounded-lg border border-[#23283b]">
                      {g.options.map((opt) => (
                        <button key={opt} onClick={() => setDiscFilter(g.key, opt)} className={discBtnClass(g.key, opt)}>
                          {g.labels[opt]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Match Counter */}
            <div className="flex items-center gap-3 py-2">
              <span className="px-3 py-1.5 bg-slate-800 border border-slate-700 text-white text-[10px] font-bold uppercase tracking-widest rounded-md flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span> {matchCount} MATCHING TARGETS
              </span>
            </div>

            {/* Discovery Layers */}
            {DISCOVERY_LAYERS.map((dl) => (
              <div key={dl.id} className="bg-[#111520] border border-[#1e2538] rounded-2xl p-6 relative overflow-hidden mt-6">
                <div className="flex items-center gap-3 mb-6 border-b border-slate-800 pb-4">
                  <div className={`w-8 h-8 rounded-lg ${dl.chipCls} border flex items-center justify-center text-xs font-black shadow-lg shrink-0`}>{dl.id}</div>
                  <h3 className="text-base font-bold text-white">{dl.title}</h3>
                </div>

                <div className={dl.grid}>
                  {dl.segments.map((seg) => (
                    <div key={seg.title} className="border border-slate-800/60 rounded-xl p-5 bg-slate-900/30 flex flex-col h-full">
                      <h4 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><span className={`w-1.5 h-1.5 ${dl.dotCls} rounded-full shrink-0`}></span> {seg.title}</h4>
                      <div className="flex flex-col gap-6 flex-1">
                        <div className="space-y-2.5">
                          <span className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest">Market Defenders</span>
                          <div className="flex flex-wrap gap-2.5">
                            {seg.defenders.map((t) => (
                              <TargetChip key={`${seg.title}-${t.name}`} t={{ ...t, type: 'defender' }} layerColor={dl.color} visible={matchTarget(t)} />
                            ))}
                          </div>
                        </div>
                        <div className="space-y-2.5 mt-auto">
                          <span className="text-[9px] font-extrabold text-slate-500 uppercase tracking-widest">AI Insurgents</span>
                          <div className="flex flex-wrap gap-2.5">
                            {seg.insurgents.map((t) => (
                              <TargetChip key={`${seg.title}-${t.name}`} t={{ ...t, type: 'insurgent' }} layerColor={dl.color} visible={matchTarget(t)} />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Transition Call-To-Action to Tab 5 */}
            <div className="bg-slate-900/60 border border-slate-800 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
              <div className="space-y-1">
                <h4 className="text-sm font-bold text-white">Targets Identified. How Do We Execute?</h4>
                <p className="text-xs text-slate-400">Advance to the final section to review the DJA Frontier Full Lifecycle Co-Execution model.</p>
              </div>
              <button
                onClick={() => setActiveTab(5)}
                className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 hover:from-cyan-400 hover:to-purple-500 text-xs font-bold uppercase tracking-wider rounded-xl shadow-lg shadow-cyan-500/20 transition-all-300 text-white flex items-center gap-2 shrink-0"
              >
                View Execution Playbook <i className="fa-solid fa-infinity"></i>
              </button>
            </div>
          </section>

          {/* ==================== TAB 5 CONTENT: CO-EXECUTION MODEL ==================== */}
          <section className={sectionClass(5)}>
            {/* Header */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center border-b border-slate-800/80 pb-8">
              <div className="lg:col-span-2 space-y-4">
                <span className="px-3 py-1 bg-purple-500/10 border border-purple-500/20 text-purple-400 rounded-full text-xs font-bold tracking-wider uppercase">Agentic Alpha Foundry</span>
                <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white leading-tight">
                  Full Lifecycle Co-Execution & Value Realization
                </h2>
                <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
                  Most strategy firms hand over an expensive slide deck of recommendations and walk away. <strong>DJA Frontier operates as a co-execution partner.</strong> We don't just advise; we underwrite high-conviction AI theses and embed alongside GFG Alliance leadership teams to manufacture structural advantages end-to-end.
                </p>
              </div>

              {/* Value Prop Box */}
              <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl space-y-2 glow-purple">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500">The DJA Frontier Promise</h3>
                <p className="text-xs text-slate-300 leading-relaxed italic">
                  "We stand shoulder-to-shoulder with GFG. From initial raw materials and energy underwritings to deploying carbon custom shields and scaling for premium exits, we are your skin-in-the-game transformation engineers."
                </p>
              </div>
            </div>

            {/* 3 Phase Lifecycle */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-6 bg-purple-500 rounded-full"></span>
                <h3 className="text-sm font-bold uppercase tracking-widest text-slate-400">The 3-Phase Execution Methodology</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Phase 1 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-cyan-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-cyan-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-compass-drafting"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 1: Sourcing Playbook & Capability Mapping</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    First, we help GFG Alliance understand the profound structural transformations reshaping heavy industries. We map the exact operational and regulatory capabilities needed to lead, underwrite high-conviction sourcing plays, and design your strategic technology roadmap.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-cyan-400 uppercase tracking-widest">
                    Target Output: Underwritten Capability Roadmap
                  </div>
                </div>

                {/* Phase 2 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-indigo-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-network-wired"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 2: Capability Acquisition & Deal Orchestration</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    Next, we help you acquire those critical capabilities. We actively orchestrate and structure the strategic transactions—whether through M&A buyouts, Joint Ventures, or exclusive vendor-partner agreements—and embed with your teams to transition them into operation.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                    Target Output: Executed Transactions & Transition
                  </div>
                </div>

                {/* Phase 3 */}
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 relative overflow-hidden flex flex-col group hover:border-purple-500/40 transition-all-300">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/5 rounded-full blur-2xl"></div>
                  <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-lg mb-5 shadow-inner">
                    <i className="fa-solid fa-chart-line"></i>
                  </div>
                  <h4 className="text-base font-black text-white mb-2">Phase 3: Value Realization & Asset Refinancing</h4>
                  <p className="text-xs text-slate-400 leading-relaxed flex-grow">
                    Ultimately, we focus on hard value creation. By proving your new physical-digital capabilities are fully embedded and CBAM/IIoT-validated, we insulate GFG's assets from macro shocks and unlock massive valuation premiums at exit or recapitalization.
                  </p>
                  <div className="mt-4 pt-4 border-t border-slate-800/60 text-[10px] font-bold text-purple-400 uppercase tracking-widest">
                    Target Output: Premium Greenfield Valuation
                  </div>
                </div>
              </div>
            </div>

            {/* Contact & Leadership CTA */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
              {/* Call to Action */}
              <div className="lg:col-span-2 bg-gradient-to-r from-slate-900 to-slate-950 border border-slate-800 p-8 rounded-2xl space-y-5 relative overflow-hidden flex flex-col justify-center">
                <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9InJnYmEoMjU1LDI1NSwyNTUsMC4wNSkiLz48L3N2Zz4=')] [mask-image:linear-gradient(to_bottom,white,transparent)]"></div>
                <h3 className="text-2xl sm:text-3xl font-black text-white relative z-10 leading-tight">Ready to Build GFG's Sourcing Engine of the Future?</h3>
                <p className="text-sm text-slate-400 max-w-xl relative z-10 leading-relaxed">Connect with the DJA Frontier partnership team to initiate a custom technology underwriting diagnostic for GFG Alliance.</p>
                <div className="pt-2 relative z-10">
                  <button className="px-8 py-3.5 bg-white text-slate-950 hover:bg-slate-200 text-sm font-bold uppercase tracking-widest rounded-xl shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all-300">
                    Initiate Strategic Dialogue
                  </button>
                </div>
              </div>

              {/* Leadership Profile Card */}
              <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl relative overflow-hidden flex flex-col items-center text-center justify-center group hover:border-purple-500/40 transition-all-300">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/20 transition-all-300"></div>

                <div className="relative mb-5 mt-2">
                  <div className="absolute inset-0 rounded-full bg-purple-500/20 blur-md animate-pulse"></div>
                  <img
                    src="\images\profile_bharat.png"
                    alt="Dr. Bharat Madan"
                    className="relative w-20 h-20 rounded-full border-2 border-purple-500/50 object-cover z-10 shadow-lg"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = 'https://placehold.co/400x400/1e293b/a855f7?text=BM'; }}
                  />
                </div>

                <div className="space-y-1.5 relative z-10">
                  <h4 className="text-lg font-black text-white tracking-tight">Dr. Bharat Madan</h4>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-purple-400 block">Managing Partner, DJA Frontier</span>
                </div>

                <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-slate-800/80 w-full relative z-10">
                  <span className="flex items-center gap-1.5 text-xs text-slate-300 font-medium">
                    <i className="fa-solid fa-location-dot text-slate-500"></i> Luxembourg
                  </span>
                  <span className="w-px h-3 bg-slate-700"></span>
                  <a href="https://www.linkedin.com/in/bharatmadan1/" target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white transition-colors font-medium">
                    <i className="fa-brands fa-linkedin text-[#0A66C2] text-sm"></i> Connect
                  </a>
                </div>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="mt-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-600 border-t border-slate-900 pt-8">
          <p>© 2026 DJA Frontier IP. Metals, Mining & Energy Strategic Decision Engine.</p>
        </footer>
      </div>
    </div>
  );
}
